import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import { SectionBreakerHorizontal, SubTitle } from '../../../components/Shared'
import { PayoutType } from './payoutTypes'
import { RADOM_COLORS } from '../../../util/Constants'
import { ManagedPaymentMethod } from '../../../util/Managed'
import Radom, { Customer, ExternalAccount, IPriceQuote, PayoutFeeEstimate } from '../../../state/Radom'
import BalanceInput from './BalanceInput'
import { InputLabel } from '../../../components/Input'
import { formatCurrency } from '../../../util/Util'
import Dropdown, { DropdownItem } from '../../../components/Dropdown'
import ExternalAccountSelectorTag from './ExternalAccountSelectorTag'
import SummaryRow from './SummaryRow'
import DetailsRow from './DetailsRow'
import { Currency } from '../../../util/Currencies'
import Spinner from '../../../components/Spinner'

interface IPayoutDetailsProps {
  payoutType: PayoutType
  selectedFundingMethod: ManagedPaymentMethod | undefined
  selectedPayoutMethod: ManagedPaymentMethod | undefined | Currency
  selectedCustomer: Customer | null
  onContinue: () => void
  onBack: () => void
  payoutAmount: string
  setPayoutAmount: (amount: string) => void
  chainName: string | undefined
  availableBalance: number
  decimals: number
  // External accounts for both fiat and crypto destination
  externalAccounts: ExternalAccount[]
  selectedExternalAccount?: ExternalAccount
  setSelectedExternalAccount: (account: ExternalAccount) => void
  // Fee state (managed by parent)
  feeEstimate: PayoutFeeEstimate | null
  setFeeEstimate: (fee: PayoutFeeEstimate | null) => void
}

const PayoutDetails = ({
  payoutType,
  selectedFundingMethod,
  selectedPayoutMethod,
  selectedCustomer,
  onContinue,
  onBack,
  payoutAmount,
  setPayoutAmount,
  chainName,
  availableBalance,
  decimals,
  externalAccounts,
  selectedExternalAccount,
  setSelectedExternalAccount,
  feeEstimate,
  setFeeEstimate
}: IPayoutDetailsProps): JSX.Element => {
  const isFiat = payoutType === PayoutType.Fiat

  // Filter external accounts by type: fiat accounts for fiat payouts, crypto for crypto
  const filteredExternalAccounts = useMemo(() =>
    externalAccounts.filter(a => isFiat ? a.accountType !== 'crypto' : a.accountType === 'crypto'),
  [externalAccounts, isFiat]
  )

  const payoutTicker = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker
    : selectedFundingMethod?.ticker || ''

  const [isLoadingFee, setIsLoadingFee] = useState(false)
  const [priceQuote, setPriceQuote] = useState<IPriceQuote | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeExternalAccountDropdown = useRef<() => void>()

  // For fiat payouts, fetch USDC → payout-currency rate; for crypto fetch USDC → USD
  const quoteToCurrency = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker.toUpperCase()
    : 'USD'

  useEffect(() => {
    Radom.getPriceQuotes(['USDC'], [quoteToCurrency])
      .then(quotes => {
        if (quotes.length > 0) {
          setPriceQuote(quotes[0])
        }
      })
      .catch(err => console.error('Failed to fetch price quote', err))
  }, [quoteToCurrency])

  const fetchFeeEstimate = useCallback(async (amount: string) => {
    if (!amount || Number(amount) <= 0) {
      setFeeEstimate(null)
      return
    }

    setIsLoadingFee(true)
    try {
      if (isFiat) {
        // Fiat fee estimation — currency must be UPPERCASE to match backend enum
        const currency = selectedPayoutMethod && 'ticker' in selectedPayoutMethod
          ? selectedPayoutMethod.ticker.toUpperCase()
          : 'EUR'
        const accountType = selectedExternalAccount?.accountOwnerType || 'individual'

        const fee = await Radom.getPayoutFee({
          currency,
          amount,
          accountType
        })
        setFeeEstimate(fee)
      } else {
        // Crypto fee estimation
        if (selectedFundingMethod) {
          const network = selectedFundingMethod.hostChain?.name || selectedFundingMethod.name
          const fee = await Radom.getPayoutFee({
            currency: 'USD',
            amount,
            network,
            token: selectedFundingMethod.hostChain?.tokenAddress || undefined
          })
          setFeeEstimate(fee)
        }
      }
    } catch (err) {
      console.error('Failed to fetch payout fee', err)
      setFeeEstimate(null)
    } finally {
      setIsLoadingFee(false)
    }
  }, [isFiat, selectedPayoutMethod, selectedExternalAccount, selectedFundingMethod, setFeeEstimate])

  // Debounced fee fetch when amount changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      fetchFeeEstimate(payoutAmount)
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [payoutAmount, fetchFeeEstimate])

  const onSelectExternalAccount = useCallback((account: ExternalAccount) => {
    setSelectedExternalAccount(account)
    closeExternalAccountDropdown.current?.()
  }, [setSelectedExternalAccount])

  const formatFeeAmount = (value: number): string => {
    if (value === 0) return '0'
    return Number(value).toFixed(2)
  }

  const hasAmount = payoutAmount && Number(payoutAmount) > 0

  // For fiat: offramp fee = total estimated fee minus the flat rail fee (percentage portion only)
  const offrampFee = feeEstimate
    ? Math.max(0, feeEstimate.estimatedFeeUsd - feeEstimate.flatFeeUsd)
    : 0

  const recipientReceives = feeEstimate
    ? `${formatFeeAmount(feeEstimate.estimatedRecipientAmount)} ${payoutTicker}`
    : hasAmount
      ? `${payoutAmount} ${payoutTicker}`
      : `0 ${payoutTicker}`

  const exchangeRateDisplay = priceQuote
    ? isFiat
      ? `1 USDC = ${Number(priceQuote.value).toFixed(4)} ${quoteToCurrency}`
      : `1 USDC = $${Number(priceQuote.value).toFixed(3)} USD`
    : isFiat
      ? `1 USDC ≈ 0.9200 ${quoteToCurrency}`
      : '1 USDC ≈ $1.00 USD'

  return (

    <div style={{ maxWidth: 560, width: '100%', display: 'flex', padding: 33, flexDirection: 'column', borderRadius: 16, border: `1px solid ${RADOM_COLORS.GRAY_DARK}`, gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ fontSize: 20, fontWeight: 400, lineHeight: '28px', margin: 0 }}>Enter Payout Details</h1>
        <SubTitle>Sending to {
          selectedCustomer?.name && selectedCustomer.name.trim().length > 0
            ? selectedCustomer.name
            : selectedCustomer?.email}</SubTitle>
      </div>
      <SectionBreakerHorizontal />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} >
        <InputLabel style={{ opacity: 1 }}>Payout Amount</InputLabel>
        <BalanceInput
          amount={payoutAmount}
          onAmount={setPayoutAmount}
          availableBalance={availableBalance}
          ticker={isFiat ? payoutTicker : (selectedFundingMethod?.ticker ?? '')} />
        <span style={{
          color: '#808080',
          fontSize: 12,
          fontWeight: 300,
          lineHeight: '16px'
        }}>
          Available balance: <span style={{ color: RADOM_COLORS.RADOM_BLUE }}>{formatCurrency(availableBalance, '', decimals)}</span> {selectedFundingMethod?.ticker} on {chainName}
        </span>
      </div>

      {/* Destination section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }} >
        <InputLabel>{isFiat ? 'Destination Bank Account' : 'Select Destination Address'}</InputLabel>

        {/* Both fiat and crypto use external accounts as destination */}
        <Dropdown
          containerStyle={{ flex: 1 }}
          onCloseFn={(fn) => { closeExternalAccountDropdown.current = fn }}
          selectedContent={
            selectedExternalAccount
              ? <ExternalAccountSelectorTag account={selectedExternalAccount} />
              : <div style={{ display: 'flex', alignItems: 'center', height: 36, opacity: 0.5, fontSize: 12, fontWeight: 500, paddingLeft: 6 }}>
                {isFiat ? 'Select bank account' : 'Select wallet address'}
              </div>
          }
          dropdownContent={
            <div>
              {filteredExternalAccounts.map((account) => (
                <DropdownItem key={account.id} onClick={() => onSelectExternalAccount(account) }>
                  <ExternalAccountSelectorTag account={account} />
                </DropdownItem>
              ))}
              {filteredExternalAccounts.length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#747894', textAlign: 'center' }}>
                  No {isFiat ? 'bank' : 'crypto'} accounts found
                </div>
              )}
            </div>
          }
        />
      </div>

      {/* Fee breakdown section — always show when amount > 0 */}
      <SectionBreakerHorizontal/>
      {hasAmount
        ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <DetailsRow title='Asset Exchange rate' value={exchangeRateDisplay} />
              <DetailsRow title='Estimated arrival' value='Instant' />
              {isLoadingFee
                ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                    <Spinner />
                    <span style={{ marginLeft: 8, fontSize: 13, color: '#747894' }}>Calculating fees...</span>
                  </div>
                )
                : feeEstimate
                  ? (
                    <>
                      {isFiat
                        ? (
                          <>
                            {offrampFee > 0 && (
                              <DetailsRow title='Offramp Fee' value={`${formatFeeAmount(offrampFee)} USDC`} />
                            )}
                            <DetailsRow title='Payment Rail Fee' value={`${formatFeeAmount(feeEstimate.flatFeeUsd)} USDC`} />
                            <DetailsRow title='Total Fee' value={`${formatFeeAmount(feeEstimate.estimatedFeeUsd)} USDC`} />
                          </>
                        )
                        : (
                          <>
                            {feeEstimate.networkFeeUsd !== undefined && feeEstimate.networkFeeUsd !== null && (
                              <DetailsRow title='Network Fee' value={`${formatFeeAmount(feeEstimate.networkFeeUsd)} USDC`} />
                            )}
                          </>
                        )
                      }
                    </>
                  )
                  : (
                    <>
                      {isFiat
                        ? (
                          <>
                            <DetailsRow title='Offramp Fee' value='--' />
                            <DetailsRow title='Payment Rail Fee' value='--' />
                          </>
                        )
                        : <DetailsRow title='Network Fee' value='--' />
                      }
                    </>
                  )
              }
            </div>
            <div>
              <SectionBreakerHorizontal/>
              {isLoadingFee
                ? <SummaryRow title="Recipient Receives" value="Calculating..." />
                : <SummaryRow title="Recipient Receives" value={recipientReceives} />
              }
            </div>
          </>
        )
        : <SummaryRow title="Total" value={`0 ${payoutTicker}`} />
      }

      <div style={{ display: 'flex', width: '100%', gap: 12 }}>
        <SecondaryButton onClick={onBack} style={{ width: '100%', border: '1px solid #D5D7E8', color: '#747894' }} >
            Back
        </SecondaryButton>

        <PrimaryButton
          onClick={onContinue}
          disabled={!payoutAmount || +payoutAmount <= 0 || !selectedExternalAccount}
          style={{ width: '100%' }}
        >
            Continue
        </PrimaryButton>
      </div>
    </div>

  )
}

export default PayoutDetails
